// src/hooks/feature/useDiscipleshipTracking.js
// MOVED: hooks/useDiscipleshipTracking.js → hooks/feature/useDiscipleshipTracking.js  (Phase 6)
// IMPORT FIX: './useSyncStore' → '@/hooks/sync/useSyncStore'

// src/hooks/useDiscipleshipTracking.js
// ─────────────────────────────────────────────────────────────
//  Discipleship tracking hook — unified with the sync layer.
//
//  FIXED:
//  • Was using localStorage.getItem('discipleshipTree') directly
//  • Now uses useSyncStore('discipleshipData') — the same key used
//    by Settings, Reports, and BibleStudyGuide
//  • Full IDB + Firestore + offline sync automatically
//
//  Migration: on first load, if 'discipleshipTree' exists in
//  localStorage but 'discipleshipData' is empty, the data is
//  migrated automatically.
// ─────────────────────────────────────────────────────────────
import { useCallback, useEffect } from 'react';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_DISCIPLESHIP_DATA } from '@/lib/storeKeys';

// Discipleship levels based on 2 Timothy 2:2
export const DISCIPLESHIP_LEVELS = {
  PAUL:         'Paul',          // You — the church planter
  TIMOTHY:      'Timothy',       // Your direct disciples
  FAITHFUL_MEN: 'Faithful Men',  // Those your Timothys are discipling
  OTHERS:       'Others',        // Third generation disciples
};

// ── Hook ─────────────────────────────────────────────────────
export function useDiscipleshipTracking() {
  // ── Use the canonical 'discipleshipData' key through the sync layer ──
  const [disciples, setDisciples, ready] = useSyncStore(STORE_DISCIPLESHIP_DATA, []);

  // ── One-time migration from legacy 'discipleshipTree' key ────────────
  useEffect(() => {
    if (!ready) return;

    const MIGRATION_KEY = 'discipleshipTree_migrated_v2';
    if (localStorage.getItem(MIGRATION_KEY)) return;

    try {
      const raw = localStorage.getItem('discipleshipTree');
      if (raw) {
        const legacy = JSON.parse(raw);
        if (Array.isArray(legacy) && legacy.length > 0) {
          // Only migrate if discipleshipData is currently empty
          setDisciples(current => {
            const currentArr = Array.isArray(current) ? current : [];
            if (currentArr.length > 0) return current; // already has data, skip

            console.info('[useDiscipleshipTracking] Migrating discipleshipTree → discipleshipData');
            return legacy;
          });
        }
      }
    } catch (err) {
      console.warn('[useDiscipleshipTracking] Migration error:', err);
    }

    localStorage.setItem(MIGRATION_KEY, '1');
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Safe array guard — discipleshipData could be null from old saves ──
  const safeDisciples = Array.isArray(disciples) ? disciples : [];

  // ── CRUD ──────────────────────────────────────────────────────────────
  // useCallback gives stable references so consumers (handleSubmit, confirmDelete,
  // etc.) don't needlessly recreate their own useCallbacks on every render.
  const addDisciple = useCallback((disciple) => {
    setDisciples(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      return [...arr, {
        ...disciple,
        id:        Date.now().toString(),
        addedDate: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }];
    });
  }, [setDisciples]);

  const updateDisciple = useCallback((id, updates) => {
    setDisciples(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map(d => d.id === id ? { ...d, ...updates, updatedAt: new Date().toISOString() } : d);
    });
  }, [setDisciples]);

  const deleteDisciple = useCallback((id) => {
    setDisciples(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      // Clear discipledBy on direct children (orphan → top-level unassigned)
      // and stamp updatedAt so the sync layer knows those records changed too.
      const now = new Date().toISOString();
      return arr
        .filter(d => d.id !== id)
        .map(d => d.discipledBy === id ? { ...d, discipledBy: null, updatedAt: now } : d);
    });
  }, [setDisciples]);

  // ── Stats ──────────────────────────────────────────────────────────────
  // useCallback([safeDisciples]) gives a stable reference that only changes
  // when disciples actually change — the useMemo in DiscipleshipKeeper that
  // wraps this was previously recomputing on every single render.
  const getMultiplicationStats = useCallback(() => {
    const timothys    = safeDisciples.filter(d => d.level === DISCIPLESHIP_LEVELS.TIMOTHY);
    const faithfulMen = safeDisciples.filter(d => d.level === DISCIPLESHIP_LEVELS.FAITHFUL_MEN);
    const others      = safeDisciples.filter(d => d.level === DISCIPLESHIP_LEVELS.OTHERS);

    const timothyStats = timothys.map(timothy => {
      const theirDisciples = faithfulMen.filter(fm => fm.discipledBy === timothy.id);
      const thirdGen       = others.filter(o =>
        theirDisciples.some(d => d.id === o.discipledBy)
      );
      return {
        ...timothy,
        discipleCount: theirDisciples.length,
        thirdGenCount:  thirdGen.length,
      };
    });

    const generations = getGenerationDepth(safeDisciples);

    return {
      totalTimothys:        timothys.length,
      totalFaithfulMen:     faithfulMen.length,
      totalOthers:          others.length,
      totalDisciples:       safeDisciples.length,
      timothyStats,
      generations,
      multiplicationFactor: safeDisciples.length > 0
        ? (safeDisciples.length / Math.max(timothys.length, 1)).toFixed(1)
        : 0,
    };
  }, [safeDisciples]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    disciples:       safeDisciples,
    addDisciple,
    updateDisciple,
    deleteDisciple,
    getMultiplicationStats,
    ready,
  };
}

// ── Pure helpers (no hook dependency) ────────────────────────

/** How many generations deep the tree goes (1 = just you, 4 = Paul→Timothy→FM→Others) */
export const getGenerationDepth = (disciples) => {
  const arr = Array.isArray(disciples) ? disciples : [];
  if (arr.some(d => d.level === DISCIPLESHIP_LEVELS.OTHERS))       return 4;
  if (arr.some(d => d.level === DISCIPLESHIP_LEVELS.FAITHFUL_MEN)) return 3;
  if (arr.some(d => d.level === DISCIPLESHIP_LEVELS.TIMOTHY))      return 2;
  return 1;
};

/** Walk up the chain from a person to the root (Paul) */
export const getDiscipleshipChain = (disciples, personId) => {
  const arr     = Array.isArray(disciples) ? disciples : [];
  const person  = arr.find(d => d.id === personId);
  if (!person) return [];

  const chain   = [person];
  const visited = new Set([person.id]); // cycle guard
  let current   = person;
  while (current.discipledBy) {
    if (visited.has(current.discipledBy)) break; // cycle detected — stop
    const mentor = arr.find(d => d.id === current.discipledBy);
    if (!mentor) break;
    visited.add(mentor.id);
    chain.unshift(mentor);
    current = mentor;
  }
  return chain;
};

/** Direct disciples of a mentor (one level down) */
export const getDirectDisciples = (disciples, mentorId) => {
  const arr = Array.isArray(disciples) ? disciples : [];
  return arr.filter(d => d.discipledBy === mentorId);
};

/** All disciples in a lineage, recursively */
export const getAllLineageDisciples = (disciples, mentorId, _visited = new Set()) => {
  if (_visited.has(mentorId)) return []; // cycle guard
  _visited.add(mentorId);
  const direct   = getDirectDisciples(disciples, mentorId);
  const indirect = direct.flatMap(d => getAllLineageDisciples(disciples, d.id, _visited));
  return [...direct, ...indirect];
};